import React from 'react';

import { StatusLozenge } from '@schoolinks/ui-components';
import { noop } from 'lodash';
import { useParams } from 'react-router-dom';

import rehypeRaw from 'rehype-raw';

import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons/faCircleInfo';

import Avatar from 'components/Common/Avatar';
import Card from 'components/Common/Card';
import Flexbox from 'components/Common/Flexbox';
import FontAwesomeIcon from 'components/Common/FontAwesomeIcon';
import LazyLoadImage from 'components/Common/LazyLoadImage';
import RenderMarkdown from 'components/Common/RenderMarkdown';
import Text from 'components/Common/Text';
import Tooltip from 'components/Common/Tooltip';
import { ShortMonthDayYearComma } from 'constants/dateTimeFormats';
import Api from 'core/api';
import useFetch from 'hooks/useFetch';
import useUserRedux from 'hooks/useUserRedux';
import dayjs from 'services/dayjs';
import { ensureObject } from 'utils/objects';

import Placeholder from 'modules/CredentialWallet/components/Placeholder';
import ShowPageHeader from 'modules/CredentialWallet/components/ShowPageHeader';
import {
  VERIFIED_TOOLTIP,
  UNVERIFIED_TOOLTIP,
  DOES_NOT_EXPIRE_TEXT,
} from 'modules/CredentialWallet/constants';

import s from './CredentialShowPage.css';

const CredentialShowPage = () => {
  const { type, credentialId, studentId } = useParams();

  const { data: user } = useUserRedux();

  const { data: credential, isLoading: isLoadingCredential } = useFetch({
    apiFunc: Api.clr.getCredentialDetails,
    apiFuncName: 'clr.getCredentialDetails',
    apiFuncParams: {
      type,
      credentialId,
    },
    filters: { student: studentId },
    options: { enabled: Boolean(user && type && credentialId) },
  });

  const {
    credentialName,
    credentialImage,
    credentialDescription,
    credentialNarrative,
    credentialAwarded,
    credentialIssuerName,
    credentialIssuerWebsite,
    credentialIssuerLogo,
    credentialIsVerified,
    credentialValidUntil,
  } = ensureObject(credential);

  return (
    <>
      <ShowPageHeader
        credentialName={credentialName}
        isPublic={false}
        setIsPublic={noop}
        setCredentialVisibility={noop}
        type={type}
        credentialId={credentialId}
        copyToClipboard={noop}
        exportCredential={noop}
        shareCredential={noop}
        hideActions
      />

      <Flexbox width="100%" p={3} className={s.content} justifyContent="center">
        <Flexbox className={s.cardContainer}>
          <Card>
            <Flexbox p={4} columnGap={4} rowGap={4} className={s.cardContents}>
              <Flexbox>
                {credentialImage ? (
                  <LazyLoadImage
                    className={s.logo}
                    src={credentialImage}
                    alt={credentialName}
                  />
                ) : (
                  <Placeholder
                    width={200}
                    height={200}
                    isLoadingCredential={isLoadingCredential}
                  />
                )}
              </Flexbox>
              <Flexbox flexDirection="column" width="100%">
                <Flexbox width="100%" mb={3}>
                  <Flexbox className={s.third} flexDirection="column">
                    <Text
                      className={s.sectionHeading}
                      variant="bodySm"
                      color="grayDark"
                    >
                      Issued date
                    </Text>
                    <Text variant="bodyMd">
                      {credentialAwarded
                        ? dayjs(credentialAwarded).format(
                            ShortMonthDayYearComma,
                          )
                        : '-'}
                    </Text>
                  </Flexbox>
                  <Flexbox className={s.third} flexDirection="column">
                    <Text
                      className={s.sectionHeading}
                      variant="bodySm"
                      color="grayDark"
                    >
                      Expiration date
                    </Text>
                    <Text variant="bodyMd">
                      {credentialValidUntil
                        ? dayjs(credentialValidUntil).format(
                            ShortMonthDayYearComma,
                          )
                        : DOES_NOT_EXPIRE_TEXT}
                    </Text>
                  </Flexbox>
                  <Flexbox className={s.third} flexDirection="column">
                    <Text
                      className={s.sectionHeading}
                      variant="bodySm"
                      color="grayDark"
                    >
                      Verification
                    </Text>
                    <Flexbox columnGap={0.5} alignItems="center">
                      <StatusLozenge
                        color={credentialIsVerified ? 'green' : 'orange'}
                        label={credentialIsVerified ? 'Verified' : 'Unverified'}
                      />
                      <Tooltip
                        title={
                          credentialIsVerified
                            ? VERIFIED_TOOLTIP
                            : UNVERIFIED_TOOLTIP
                        }
                        placement="bottom"
                      >
                        <div>
                          <FontAwesomeIcon
                            icon={faCircleInfo}
                            color="grayDark"
                            size="lg"
                          />
                        </div>
                      </Tooltip>
                    </Flexbox>
                  </Flexbox>
                </Flexbox>
                <Text
                  className={s.sectionHeading}
                  variant="bodySm"
                  color="grayDark"
                >
                  Issuer
                </Text>
                <Flexbox columnGap={1} alignItems="center" mb={3}>
                  <Avatar size="md" src={credentialIssuerLogo} />
                  <Flexbox flexDirection="column">
                    <Text variant="bodyMd">{credentialIssuerName}</Text>
                    {credentialIssuerWebsite && (
                      <Text variant="metaBold">{credentialIssuerWebsite}</Text>
                    )}
                  </Flexbox>
                </Flexbox>
                <Text
                  className={s.sectionHeading}
                  variant="bodySm"
                  color="grayDark"
                >
                  Description
                </Text>
                <Text variant="bodyMd" mb={3}>
                  {credentialDescription ? (
                    <RenderMarkdown rehypePlugins={[rehypeRaw]}>
                      {credentialDescription}
                    </RenderMarkdown>
                  ) : (
                    '-'
                  )}
                </Text>
                <Text
                  className={s.sectionHeading}
                  variant="bodySm"
                  color="grayDark"
                >
                  Narrative
                </Text>
                <Text variant="bodyMd">
                  {credentialNarrative ? (
                    <RenderMarkdown rehypePlugins={[rehypeRaw]}>
                      {credentialNarrative}
                    </RenderMarkdown>
                  ) : (
                    '-'
                  )}
                </Text>
              </Flexbox>
            </Flexbox>
          </Card>
        </Flexbox>
      </Flexbox>
    </>
  );
};

export default CredentialShowPage;
